<!--
  Line map widget shared by `line_map#show` (full UI: route list + detail panel)
  and `users#show` (map only, `nopad=true`).

  The legacy `line_map#show` rendered the whole panel server-side inside `#vue`
  and mounted a Vue 2 instance over it. `users#show` rendered only `#map.map`
  and called `new LineMap().init(true)` (nopad). Both paths now mount this
  component on `#line-map-app`; `data-with-panel` selects the UI variant.
-->
<template>
  <!--
    `withPanel=true` (`line_map#show`) keeps the legacy full-screen layout:
    `.container-fluid.map-container` > `.col-12.nopad.map-col` (fixed via
    `line_map.sass`) > `#map.map`.

    `withPanel=false` (`users#show`) must match the legacy DOM, which was
    `.card > #map.map` inside `users/show.html.slim`; the full-screen wrappers
    above are `line_map#show`-only and changed the map's width/height. Render
    the bare `#map.map` in that branch so `users.sass`'s
    `.users-show .map { margin-bottom: -5px }` keeps working as before.
  -->
  <div v-if="withPanel" class="container-fluid map-container">
    <div
      v-if="withPanel"
      class="col-12 col-md-4 nopad detail-col card"
      :class="open ? '' : 'closed'"
    >
      <div class="card-body">
        <div class="progress">
          <div class="progress-bar" :style="progress_style"></div>
        </div>
        <div class="progress">
          <div class="progress-bar" :style="detail_progress_style"></div>
        </div>
        <div class="d-flex clearfix switcher" @click="toggleDetail">
          <div class="mr-auto">
            <div class="profile">
              <img v-if="userImageUrl" :src="userImageUrl" class="icon" :alt="userName" />
              <img v-else :src="dropIconUrl" class="icon drop" alt="" />
              <span class="name">{{ userName }}</span>
            </div>
            <div>
              <span class="distance mr-2">{{ total_distance }}km</span>
              <span class="up-total">{{ total_up }}up</span>
            </div>
          </div>
          <div class="ml-3 text-black-secondary">
            <i v-if="(!open && !is_mobile) || (open && is_mobile)" class="material-icons">
              keyboard_arrow_down
            </i>
            <i v-else class="material-icons">keyboard_arrow_up</i>
          </div>
        </div>
        <div v-if="open" id="detail">
          <div class="col-12 nopad d-flex justify-content-start">
            <div class="btn-group">
              <button
                v-if="editable"
                id="add"
                class="btn btn-light my-auto"
                @click="openAddModal"
              >
                <i class="material-icons">add</i>
              </button>
              <button class="btn btn-light" @click="resetExtent">
                <i class="material-icons">replay</i>
              </button>
              <div class="btn-group my-auto">
                <button
                  id="switchMap"
                  class="btn dropdown-toggle btn-light"
                  data-toggle="dropdown"
                  aria-expanded="false"
                  aria-haspopup="true"
                  role="button"
                >
                  <i class="material-icons">layers</i>
                </button>
                <div class="dropdown-menu" aria-labelledby="#switchMap">
                  <a
                    v-for="(bm, id) in backgroundMaps"
                    :key="id"
                    class="dropdown-item"
                    @click.prevent="changeBackgroundMap(id)"
                  >
                    {{ bm.title }}
                  </a>
                </div>
              </div>
              <div class="btn-group my-auto">
                <button
                  id="sortMenu"
                  class="btn dropdown-toggle btn-light"
                  data-toggle="dropdown"
                  aria-expanded="false"
                  aria-haspopup="true"
                  role="button"
                >
                  <i class="material-icons">sort</i>
                  <span class="mx-1">{{ sortLabel }}</span>
                </button>
                <div class="dropdown-menu" aria-labelledby="#sortMenu">
                  <a
                    v-for="item in sortKeys"
                    :key="item.key"
                    href="#"
                    class="dropdown-item"
                    @click.prevent="setSort(item.key)"
                  >
                    {{ item.label }}
                    <i v-if="sortKey === item.key" class="material-icons float-right">
                      {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 mt-3 nopad">
            <input
              v-model="searchQuery"
              class="form-control"
              type="text"
              placeholder="タイトルで検索"
            />
          </div>
          <div v-if="routes.length === 0 && fetching == 0" class="col-12 mb-2 mt-3 nopad">
            <span>まだ線がないようです</span>
          </div>
          <div v-else-if="fetching > 0" class="col-12 mb-2 mt-3 nopad">
            <span>読み込み中...</span>
          </div>
          <div v-else-if="displayedRoutes.length === 0" class="col-12 mb-2 mt-3 nopad">
            <span>該当する線がありません</span>
          </div>
          <template v-else>
            <div
              v-for="route in displayedRoutes"
              :key="route.id"
              class="col-12 mb-2 nopad mt-3"
            >
              <div class="card" :style="'border-left: 0.5rem solid ' + route.color + ';'">
                <div class="card-body">
                  <div
                    class="route-header d-flex justify-content-between"
                    @click="toggleOpenRoute(route.id)"
                  >
                    <div class="route-switcher mr-auto w-100">
                      <template v-if="editable">
                        <div v-if="route.open" class="route-title">
                          <input
                            v-model="route.title"
                            class="form-control title"
                            type="text"
                            @click.stop
                            @input="changeTitle(route.id)"
                          />
                        </div>
                        <div v-else class="route-title">{{ route.title }}</div>
                      </template>
                      <div v-else class="route-title">{{ route.title }}</div>
                      <div class="route-description">
                        <i
                          v-if="route.members_only"
                          class="material-icons lock"
                          title="部内公開"
                        >
                          lock
                        </i>
                        {{ route.distance }}km / {{ route.up }}up
                      </div>
                    </div>
                    <div class="route-check" @click.prevent.stop="toggleSelectRoute(route.id)">
                      <i class="material-icons">
                        {{ route.selected ? 'check_circle' : 'radio_button_unchecked' }}
                      </i>
                    </div>
                  </div>
                  <div v-if="route.open" class="route-action mt-2">
                    <template v-if="editable">
                      <div class="colors">
                        <div
                          v-for="color in colors"
                          :key="color"
                          class="color-sample"
                          :style="'background-color: ' + color + ';'"
                          @click.prevent="changeColor(route.id, color)"
                        ></div>
                      </div>
                      <div class="d-flex mt-2 align-items-center">
                        <label class="mr-2 mb-0 text-nowrap flex-shrink-0">表示順</label>
                        <input
                          v-model.lazy="route.display_order"
                          class="form-control display-order"
                          type="text"
                          maxlength="32"
                          @change="changeDisplayOrder(route.id, $event)"
                        />
                      </div>
                      <div class="d-flex mt-2 align-items-center">
                        <label class="mr-2 mb-0 text-nowrap flex-shrink-0">部内公開</label>
                        <input
                          v-model="route.members_only"
                          class="members-only"
                          type="checkbox"
                          @change="changeMembersOnly(route.id)"
                        />
                      </div>
                      <div class="d-flex justify-content-between">
                        <div class="mr-auto">
                          <input
                            v-model="route.color"
                            class="form-control color"
                            type="text"
                            :style="checkColor(route.color) ? 'color:' + route.color : 'black'"
                            @input="changeColor(route.id)"
                          />
                        </div>
                        <div>
                          <button id="delete" class="btn btn-outline-danger" @click="removeRoute(route.id)">
                            <i class="material-icons">delete</i>
                          </button>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <a v-if="loggedIn" :href="`/line_map/copy?id=${route.id}`">パクる</a>
                      <span v-else>ログインするとパクれます</span>
                    </template>
                  </div>
                  <div class="route-link mt-2">
                    {{ provider_name(route.provider) }}:
                    <a
                      :href="provider_link(route.provider, route.route_id)"
                      target="_blank"
                      :download="`${route.title}.gpx`"
                    >
                      {{ route.title }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="col-12 nopad map-col" :class="withPanel && !open ? 'closed' : ''">
      <div id="map" ref="map" class="map"></div>
    </div>
    <AddRouteModal
      v-if="withPanel && editable"
      :open="addModalOpen"
      file-label="またはGPXファイルをアップロード(ベータ)"
      @close="closeAddModal"
      @send="onClickSend"
    />
  </div>
  <div v-else id="map" ref="map" class="map"></div>
</template>

<script>
import { ajax, postForm } from '../../lib/api'
import AddRouteModal from '../../lib/line-map/AddRouteModal.vue'
import {
  sortRoutes,
  filterRoutes,
  buildSortLabel,
  providerName,
  providerLink,
  checkColor,
  convertColorToRgb,
  readSortKey,
  readSortOrder,
  writeSortKey,
  writeSortOrder,
} from '../../lib/line-map/logic'
import { getGpxes } from '../../lib/line-map/api'
import { createMapManager } from '../../lib/line-map/map'
import { confirmDialog } from '../../lib/confirm'

export default {
  name: 'LineMapApp',

  components: { AddRouteModal },

  // The view renders these on the mount element as `data-*` attributes and the
  // entrypoint passes them as props (see `entrypoints/line-map.js` and #67).
  props: {
    mapId: { type: String, default: '' },
    withPanel: { type: Boolean, default: false },
    editable: { type: Boolean, default: false },
    loggedIn: { type: Boolean, default: false },
    userName: { type: String, default: '' },
    userImageUrl: { type: String, default: '' },
    dropIconUrl: { type: String, default: '' },
  },

  data() {
    return {
      routes: [],
      sortKey: readSortKey(localStorage),
      sortOrder: readSortOrder(localStorage),
      sortKeys: [
        { key: 'display_order', label: '表示順' },
        { key: 'created_at', label: '追加日時' },
        { key: 'distance', label: '距離' },
        { key: 'up', label: '獲得標高' },
      ],
      searchQuery: '',
      open: true,
      addModalOpen: false,
      backgroundMaps: {
        none: {
          title: 'なし',
          createSource: function () {
            return null
          },
        },
        mapBox: {
          title: 'MapBox Outdoors',
          createSource: function () {
            return new ol.source.XYZ({
              attributions:
                "© <a href='https://www.mapbox.com/about/maps/'>Mapbox</a> © <a href='http://www.openstreetmap.org/copyright'>OpenStreetMap</a>",
              url: 'https://api.mapbox.com/styles/v1/zatton/cjvk70irv1lhz1cpand1mm54f/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1IjoiemF0dG9uIiwiYSI6ImNpeXA2eHI0aTAwMnU0ZHF6Mzh6NmVwaXMifQ.CVgzva-xVFn0DBLjjEYhog',
              sphericalMercator: true,
              wrapDateLine: true,
            })
          },
        },
        osm: {
          title: 'Open Street Map',
          createSource: function () {
            return new ol.source.OSM()
          },
        },
        plain: {
          title: '白地図',
          createSource: function () {
            return new ol.source.XYZ({
              attributions:
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>地理院タイル</a>",
              url: 'http://cyberjapandata.gsi.go.jp/xyz/blank/{z}/{x}/{y}.png',
              projection: 'EPSG:3857',
            })
          },
        },
        gsi: {
          title: '国土地理院地図',
          createSource: function () {
            return new ol.source.XYZ({
              attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>地理院タイル</a>",
              ],
              url: 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png',
              projection: 'EPSG:3857',
            })
          },
        },
        gsiPlain: {
          title: '国土地理院地図(淡色)',
          createSource: function () {
            return new ol.source.XYZ({
              attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>地理院タイル</a>",
              ],
              url: 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png',
              projection: 'EPSG:3857',
            })
          },
        },
      },
      selectedBackgroundMapId: 'mapBox',
      // `$(window).width()` in the legacy code is `document.documentElement.clientWidth`
      // (scrollbar excluded); `window.innerWidth` includes it.
      is_mobile: document.documentElement.clientWidth < 768,
      colors: [
        '#ff0000',
        //"#ff1f00",
        '#ff7c00',
        '#f52394',
        '#800080',
        '#0000ff',
        '#0062c6',
        '#01b2d4',
        '#00ab00',
        '#90e200',
        '#ffce00',
        '#7f00ff',
        '#68dad6',
        '#c4c4c4',
        '#593010',
        '#000000',
      ],
      queue_length: 0,
      queue_done: 0,
      fetching: 0,
      fetching_limit: 30,
      pending_list: [],
      detail_queue_length: 0,
      detail_queue_done: 0,
      detail_fetching: 0,
      detail_fetching_limit: 30,
      detail_pending_list: [],
    }
  },

  computed: {
    total_distance() {
      return this.routes.reduce(function (acc, cur) {
        return acc + cur.distance
      }, 0)
    },
    total_up() {
      return this.routes.reduce(function (acc, cur) {
        return acc + cur.up
      }, 0)
    },
    sortedRoutes() {
      return sortRoutes(this.routes, this.sortKey, this.sortOrder)
    },
    displayedRoutes() {
      return filterRoutes(this.sortedRoutes, this.searchQuery)
    },
    sortLabel() {
      return buildSortLabel(this.sortKeys, this.sortKey, this.sortOrder)
    },
    progress_style() {
      if (this.queue_length !== 0) {
        return `width: ${(this.queue_done / this.queue_length) * 100}%;`
      } else {
        return 'opacity: 0'
      }
    },
    detail_progress_style() {
      if (this.detail_queue_length !== 0) {
        return `width: ${(this.detail_queue_done / this.detail_queue_length) * 100}%;`
      } else {
        return 'opacity: 0'
      }
    },
  },

  mounted() {
    const self = this
    const el = self.$refs.map

    self.mapManager = createMapManager(self, {
      target: el,
      withPanel: self.withPanel,
      getPadding: function () {
        if (!self.withPanel) return 0
        return self.is_mobile ? 0 : document.documentElement.clientWidth * 0.3333 + 30
      },
    })
    self.resizeHandler = function () {
      self.mapManager.map.updateSize()
      self.is_mobile = document.documentElement.clientWidth < 768
    }
    window.addEventListener('resize', self.resizeHandler)

    // Abort in-flight requests before Turbo swaps the page (the legacy
    // `$(document).one('turbo:before-visit', () => ajax.abort())`). The Vue app
    // itself is unmounted on the next `turbo:load`.
    self.beforeVisitHandler = function () {
      self.mapManager && self.mapManager.abortRequests()
    }
    document.addEventListener('turbo:before-visit', self.beforeVisitHandler)

    self.fetching = self.fetching + 1
    const controller = new AbortController()
    self.mapManager.registerRequest(controller)
    getGpxes(self.mapId, controller.signal)
      .then(function (data) {
        self.fetching = self.fetching - 1
        self.routes = data
        self.queue(data)
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return
        self.fetching = self.fetching - 1
      })
  },

  updated() {
    this.mapManager && this.mapManager.updateLayerStyles(this.routes)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.resizeHandler)
    document.removeEventListener('turbo:before-visit', this.beforeVisitHandler)
    this.mapManager && this.mapManager.destroy()
    this.mapManager = null
  },

  methods: {
    provider_name(provider) {
      return providerName(provider)
    },
    provider_link(provider, route_id) {
      return providerLink(provider, route_id)
    },
    provider_file(provider) {
      if (provider === 'RouteLab' || provider === 'file') {
        return 'route.gpx'
      } else {
        return ''
      }
    },
    checkColor(color) {
      return checkColor(color)
    },
    convert_color_to_rgb(color) {
      return convertColorToRgb(color)
    },
    judge_font_color(color) {
      // NOTE: `self` is not defined in this method in the original
      // CoffeeScript, so it resolves to the global `self` (window.self).
      // Behaviour is kept as-is (it would throw here).
      const ref = self.convert_color_to_rgb(color)
      const re = ref[0],
        gr = ref[1],
        bl = ref[2]
      const lightness = (re * 0.299 + gr * 0.587 + bl * 0.114) / 2.55
      if (lightness >= 50) {
        return 'black'
      }
      return 'white'
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'asc'
      }
      writeSortKey(localStorage, this.sortKey)
      writeSortOrder(localStorage, this.sortOrder)
    },
    toggleDetail() {
      this.open = !this.open
    },
    changeBackgroundMap(id) {
      this.mapManager.changeBackgroundMap(id)
      this.selectedBackgroundMapId = id
    },
    resetExtent() {
      this.mapManager.resetExtent()
      this.clearRouteState()
    },
    clearRouteState() {
      this.routes.forEach(function (route) {
        route.open = false
        route.selected = false
      })
    },
    toggleOpenRoute(id) {
      this.queueDetail(id)
      this.routes.forEach(function (route) {
        if (route.id === id) {
          route.open = !route.open
          if (route.open) {
            route.selected = true
          }
        } else {
          route.open = false
        }
      })
      this.mapManager.updateExtent(this.routes)
    },
    toggleSelectRoute(id) {
      this.queueDetail(id)
      const route = this.routes.find(function (r) {
        return r.id === id
      })
      if (!route) return
      route.selected = !route.selected
      if (!route.selected) {
        route.open = false
      }
      if (route.open) {
        this.mapManager.updateExtent(this.routes)
      }
    },
    queue(data) {
      this.queue_length = data.length
      this.queue_done = 0
      const self = this
      data.forEach(function (d) {
        self.addRoute(d.id, d.route_id, d.color, d.title)
      })
    },
    addRoute(id, route_id, color, title) {
      const self = this
      if (self.fetching > self.fetching_limit) {
        self.pending_list.push([id, route_id, color, title])
        return
      }
      self.mapManager.addRouteLayer(id, color)
      // Legacy `addRoute` incremented `fetching` before the AJAX call and
      // decremented it in `complete` (i.e. also on failure/abort).
      self.fetching = self.fetching + 1
      const controller = new AbortController()
      self.mapManager.registerRequest(controller)
      self.mapManager
        .loadRoute(`/kml_compressed/${route_id}.kml`, id, title, controller.signal)
        .then(
          function () {
            self.finishRoute()
          },
          function () {
            self.finishRoute()
          }
        )
    },
    finishRoute() {
      const self = this
      self.fetching = self.fetching - 1
      self.queue_done = self.queue_done + 1
      if (self.pending_list.length > 0) {
        const _route = self.pending_list.shift()
        self.addRoute(_route[0], _route[1], _route[2], _route[3])
      }
      if (self.queue_done === self.queue_length) {
        self.queue_done = 0
        self.queue_length = 0
      }
    },
    queueDetail(id) {
      const route = this.routes.find(function (r) {
        return r.id === id
      })
      if (!route || route.detail_queued) return
      route.detail_queued = true
      this.detail_queue_length = this.detail_queue_length + 1
      this.loadDetailRoute(id)
    },
    loadDetailRoute(id) {
      const self = this
      const route = self.routes.find(function (r) {
        return r.id === id
      })
      if (!route || route.detail_loaded) return
      if (self.detail_fetching > self.detail_fetching_limit) {
        self.detail_pending_list.push(id)
        return
      }
      route.detail_loaded = true
      // Legacy `loadDetailRoute` incremented `detail_fetching` before the AJAX
      // call and decremented it in `complete` (i.e. also on failure/abort).
      self.detail_fetching = self.detail_fetching + 1
      const controller = new AbortController()
      self.mapManager.registerRequest(controller)
      self.mapManager
        .loadKmlDetails(`/kml/${route.route_id}.kml`, id, controller.signal)
        .then(
          function () {
            self.finishDetailRoute()
          },
          function () {
            self.finishDetailRoute()
          }
        )
    },
    finishDetailRoute() {
      const self = this
      self.detail_fetching = self.detail_fetching - 1
      self.detail_queue_done = self.detail_queue_done + 1
      if (self.detail_pending_list.length > 0) {
        const _id = self.detail_pending_list.shift()
        self.loadDetailRoute(_id)
      }
      if (self.detail_queue_done === self.detail_queue_length) {
        self.detail_queue_done = 0
        self.detail_queue_length = 0
      }
    },
    changeTitle(route_id) {
      for (const route of this.routes) {
        if (route.id === route_id) {
          const data = new FormData()
          data.append('id', route_id)
          data.append('title', route.title)
          ajax(`/linemap/${route_id}`, { method: 'PATCH', data, parse: 'none' }).catch(function () {})
        }
      }
    },
    changeDisplayOrder(route_id, event) {
      const route = this.routes.find(function (r) {
        return r.id === route_id
      })
      if (!route) return
      const value = event && event.target ? event.target.value : route.display_order
      route.display_order = value
      const data = new FormData()
      data.append('id', route_id)
      data.append('display_order', value)
      ajax(`/linemap/${route_id}`, { method: 'PATCH', data, parse: 'json' })
        .then(function (response) {
          if (response && response.display_order) {
            route.display_order = response.display_order
          }
        })
        .catch(function () {})
    },
    changeMembersOnly(route_id) {
      const route = this.routes.find(function (r) {
        return r.id === route_id
      })
      if (!route) return
      const data = new FormData()
      data.append('id', route_id)
      data.append('members_only', route.members_only)
      ajax(`/linemap/${route_id}`, { method: 'PATCH', data, parse: 'none' }).catch(function () {})
    },
    changeColor(route_id, color) {
      for (const route of this.routes) {
        if (route.id === route_id) {
          if (route.color === '') {
            route.color = '#'
          }
          if (!color) {
            color = route.color
          }
          if (!checkColor(color)) return
          route.color = color
          this.mapManager.changeColor(route.id, route.color)
          const data = new FormData()
          data.append('id', route_id)
          data.append('color', color)
          ajax(`/linemap/${route_id}`, { method: 'PATCH', data, parse: 'none' }).catch(function () {})
        }
      }
    },
    openAddModal(e) {
      e.preventDefault()
      this.addModalOpen = true
    },
    closeAddModal() {
      this.addModalOpen = false
    },
    onClickSend({ url, files } = {}) {
      const self = this
      self.closeAddModal()
      // The legacy modal looked these two up by id, but no such inputs were
      // ever rendered, so they were always `undefined` (and `FormData.append`
      // turned that into the string "undefined"). Keep the same values.
      const ele_th = undefined
      const dist_th = undefined
      const maxIndex = Math.max(0, files.length - 1)
      let file
      let color
      for (let fidx = 0; fidx <= maxIndex; fidx++) {
        file = files.length > 0 ? files[fidx] : null
        color = '#'
        for (let i = 1; i <= 6; i++) {
          color += parseInt(Math.random() * 16).toString(16)
        }
        const fd = new FormData()
        fd.append('url', url)
        fd.append('color', color)
        fd.append('gpx', file)
        fd.append('dist_th', dist_th)
        fd.append('ele_th', ele_th)
        const controller = new AbortController()
        self.mapManager.registerRequest(controller)
        postForm('/linemap', fd, controller.signal)
          .then(function (data) {
            self.addRoute(data.id, data.route_id, color, data.title)
            data.selected = true
            data.open = true
            data.detail_queued = true
            data.detail_loaded = true
            self.routes.forEach(function (route) {
              route.open = false
            })
            self.routes.unshift(data)
          })
          .catch(function (error) {
            if (error && error.name === 'AbortError') return
            alert('エラー')
          })
      }
    },
    async removeRoute(route_id) {
      const self = this
      for (let index = 0; index < self.routes.length; index++) {
        const route = self.routes[index]
        if (route.id === route_id) {
          if (!(await confirmDialog(`${route.title}を削除しますか？`))) return
          const data = new FormData()
          data.append('id', route.id)
          ajax(`/linemap/${route.id}`, { method: 'DELETE', data, parse: 'none' }).catch(function () {})
          self.mapManager.removeRouteLayer(route.id)
          self.routes.splice(index, 1)
          return
        }
      }
    },
    sleep(ms) {
      const start = new Date().getTime()
      while (new Date().getTime() - start < ms) continue
    },
  },
}
</script>
